import React from "react";
import { useParams } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import { NursingHomeHeader } from "@/components/nursing-homes/NursingHomeHeader";
import { NursingHomeContent } from "@/components/nursing-homes/NursingHomeContent";
import { useNursingHome } from "@/hooks/use-nursing-home";
import { SEOHead } from "@/components/seo/SEOHead";
import { generateNursingHomeSchema, generateBreadcrumbSchema } from "@/lib/schema";

export default function NursingHomePage() {
  const { countySlug, nursing_home_slug } = useParams();
  console.log('NursingHomePage params:', { countySlug, nursing_home_slug });
  const { data: nursingHome, isLoading } = useNursingHome(nursing_home_slug);
  console.log('NursingHomePage data:', { nursingHome, isLoading });

  // Format county name
  const formatCountyName = (slug?: string) => {
    if (!slug) return '';
    return slug
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Create breadcrumbs for this page when data is available
  const breadcrumbs = nursingHome ? [
    { name: "Home", url: "/" },
    { name: "Nursing Homes", url: "/nursing-homes" },
    { name: formatCountyName(countySlug), url: `/nursing-homes/${countySlug}` },
    { name: nursingHome.center_name || "Nursing Home", url: `/nursing-homes/${countySlug}/${nursing_home_slug}` }
  ] : [];

  // Get the absolute URL for this nursing home
  const currentUrl = nursingHome && countySlug && nursing_home_slug
    ? `https://www.grandpal.co/nursing-homes/${countySlug}/${nursing_home_slug}`
    : '';

  // Use either new or old property names based on availability
  const getAddressInfo = () => {
    if (!nursingHome) return {
      streetAddress: "",
      addressLocality: "",
      addressRegion: "",
      postalCode: "",
      addressCountry: "Ireland"
    };
    
    return {
      streetAddress: nursingHome.street_name || nursingHome.street_address || "",
      addressLocality: nursingHome.town || nursingHome.city_town || "",
      addressRegion: nursingHome.county || nursingHome.county_name || "",
      postalCode: nursingHome.eircode || nursingHome.eir_code || "",
      addressCountry: "Ireland"
    };
  };

  // Generate schemas if we have the nursing home data
  const schemas = nursingHome ? [
    generateNursingHomeSchema({
      name: nursingHome.center_name || "",
      description: nursingHome.description || `${nursingHome.center_name} is a nursing home located in ${formatCountyName(countySlug)}, Ireland.`,
      address: getAddressInfo(),
      telephone: nursingHome.phone_number || "",
      url: currentUrl,
      imageUrl: nursingHome.main_image_url,
      geo: nursingHome.latitude && nursingHome.longitude ? {
        latitude: nursingHome.latitude,
        longitude: nursingHome.longitude
      } : undefined,
      priceRange: nursingHome.price_range || "€€€",
      openingHours: "24/7",
      dateModified: nursingHome.last_updated_at
        ? new Date(nursingHome.last_updated_at).toISOString().split('T')[0]
        : undefined,
    }),
    generateBreadcrumbSchema(breadcrumbs)
  ] : [];

  const countyName = nursingHome ? (nursingHome.county || nursingHome.county_name || formatCountyName(countySlug)) : formatCountyName(countySlug);

  const pageTitle = nursingHome ? 
    `${nursingHome.center_name} | Nursing Home in ${countyName} | Grandpal` :
    'Loading Nursing Home Details | Grandpal';
  
  const pageDescription = nursingHome ?
    `${nursingHome.center_name} is a nursing home located in ${countyName}, Ireland. Find detailed information about facilities, services, and contacts.` :
    'Loading detailed information about this nursing home in Ireland.';

  // Create breadcrumb items for the UI component
  const breadcrumbItems = breadcrumbs.map(item => ({
    label: item.name,
    href: item.url
  }));

  return (
    <Layout>
      <SEOHead 
        title={pageTitle}
        description={pageDescription}
        canonicalUrl={currentUrl || `/nursing-homes/${countySlug}/${nursing_home_slug}`}
        ogImage={nursingHome?.main_image_url || "/lovable-uploads/b67e8743-f24e-45cd-ba77-2a1ae609411c.png"}
        schema={schemas}
      />

      {!isLoading && nursingHome && (
        <>
          <NursingHomeHeader 
            centerName={nursingHome.center_name} 
            countyName={countyName}
            countySlug={countySlug}
            description={nursingHome.description || `A nursing home located in ${formatCountyName(countySlug)}, Ireland.`}
            shortDescription={nursingHome.short_description}
            maxResidents={nursingHome.max_residents}
            category={nursingHome.category}
            nhSlug={nursingHome.nh_slug}
            address={nursingHome.standardized_address}
            imageUrl={nursingHome.main_image_url}
            phone={nursingHome.phone_number}
            latitude={nursingHome.latitude}
            longitude={nursingHome.longitude}
            breadcrumbItems={breadcrumbItems}
            fairDealApproved={nursingHome.fair_deal_approved}
            inspectionRating={nursingHome.inspection_rating}
            wheelchairAccessible={nursingHome.wheelchair_accessible}
            specializations={nursingHome.specializations}
            priceRange={nursingHome.price_range}
          />
          <NursingHomeContent 
            nursingHome={{
              ...nursingHome,
              county_slug: countySlug || '', // Ensure county_slug is always provided
              hiqa_profile_url: nursingHome.hiqa_profile_url,
              email: nursingHome.email
            }} 
            activeTabProp="overview" // Explicitly set tab
          />
        </>
      )}

      {isLoading && (
        <div className="container py-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-grandpal-green mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading nursing home details...</p>
        </div>
      )}
    </Layout>
  );
}
